/** Badge: a small word on a tag ("Dry run", "Reviewed"). Count: a number in a pill (a queue's open count, the filters in use). */
import type { ReactNode } from "react";
import { cx } from "./cx";
import { Icon, type IconName } from "./icons";

/** A tone is the reference's status icon beside the word, never a coloured word: a coloured word on the tag would be status by colour alone. */
const TONE_ICON: Record<"success" | "warning" | "destructive", IconName> = {
  success: "status-delivered",
  warning: "status-progress",
  destructive: "feed-alert",
};

export function Badge({ children, tone = "neutral", className }: { children: ReactNode; tone?: "neutral" | keyof typeof TONE_ICON; className?: string }) {
  return (
    <span className={cx("inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border-hair border-border bg-secondary px-2 text-caption font-medium leading-none text-secondary-foreground", className)}>
      {tone !== "neutral" && <Icon name={TONE_ICON[tone]} size={14} />}
      {children}
    </span>
  );
}

const COUNT = {
  /** The dark fill: something is waiting. */
  solid: "bg-primary text-primary-foreground",
  /** A plain tally. */
  quiet: "bg-accent text-secondary-foreground",
};

interface CountProps {
  value: number;
  tone?: keyof typeof COUNT;
  /** What is counted, for assistive tech ("71 open"). */
  label?: string;
  className?: string;
}

/** Written at once, in its final ink: a count that fades or pops in reads fainter in one frame than the next. */
export function Count({ value, tone = "solid", label, className }: CountProps) {
  return (
    <span aria-label={label} className={cx("inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-caption font-medium leading-none tabular", COUNT[tone], className)}>
      {value.toLocaleString("en-US")}
    </span>
  );
}
